import { PropsWithChildren } from 'react';
import { Alert, Button } from '@mui/material';
import { Navigate } from 'react-router';
import { LoaderContainer } from '../components/atoms/LoaderContainer';
import CasinoSignInScreen from '../components/organisms/casino/CasinoSignInScreen';
import useProductAvailability from '../hooks/useProductAvailability';
import useLocalization from '../hooks/useLocalization';
import { useCasinoRouteAuthentication } from '../hooks/components/useCasinoRouteAuthentication';

type Props = {
  redirect?: string;
};

const CasinoAccessGuard = ({ redirect = '/', children }: PropsWithChildren<Props>) => {
  const {
    hasCasinoAccess,
    requiresCasinoSignIn,
    canViewCasinoContent,
    hasCasinoAccessError,
    isCasinoAccessLoading,
    isAuthenticated,
    retryCasinoAccess,
    isRetryingCasinoAccess,
  } = useProductAvailability();
  const { t } = useLocalization();
  useCasinoRouteAuthentication({ isAuthenticated, isContentBlocked: !canViewCasinoContent });

  if (hasCasinoAccessError) {
    return (
      <Alert
        severity='error'
        action={
          <Button color='inherit' disabled={isRetryingCasinoAccess} onClick={() => void retryCasinoAccess()}>
            {t('retry')}
          </Button>
        }
      >
        {t('casinoLoadFailed')}
      </Alert>
    );
  }

  if (isCasinoAccessLoading) {
    return <LoaderContainer />;
  }

  if (!hasCasinoAccess) {
    return <Navigate to={redirect} replace />;
  }

  if (requiresCasinoSignIn) {
    return <CasinoSignInScreen />;
  }

  return <>{children}</>;
};

export default CasinoAccessGuard;
